import { useEffect, useState } from "react";
import useTitanApp from "@/hooks/useTitanApp";
import { generateAiTemplate } from "@/dispatchers/templateDispatchers";
import {
  generateAiServiceTemplate,
  saveAiTemplate,
} from "@/dispatchers/patientDispatchers";
import AiStreamResponse from "@/utils/AiStreamResponse";
import { generateAiServiceTemplateUrl } from "@/apiUrls/apiUrls";
import {
  updateAiTemplates,
  getAiTemplateByCompanyId,
  setIsConsultationSummaryLoading,
  setConsultationStreamedSummary,
} from "@/dispatchers/templateDispatchers";
import { getPatientConsultationsCount } from "../../dispatchers/patientDispatchers";

export interface ConsultationTemplate {
  id: string;
  name: string;
  companyID: string;
  description: string;
  consultationSummary: string;
  category: "general" | "specialty" | "follow-up" | "emergency";
  sections: ConsultationSection[];
  freeTextContent?: string;
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
  createdBy: string;
  createdByName: string;
  updatedAt: string;
  updatedBy: string;
  updatedByName: string;
  archiveAt: string;
  archiveBy: string;
  archiveByName: string;
  usageCount: number;
  isOwned?: boolean; // true for user's templates, false/undefined for community
  isArchive?: boolean; // true for user's templates, false/undefined for community
  tempPrompt: string;
  linkedDocumentId?: string;
  forceUserSignOff?: boolean;
  lastReviewedAt?: string;
  reviewNote?: string;
  consultationActions?: ConsultationAction[];
  treatmentPlan?: TreatmentPlanTemplate;
}

export interface TreatmentPlanTemplate {
  treatmentPlanConfigurationId?: string;
  appointments: TreatmentPlanAppointment[];
}

export interface TreatmentPlanTask {
  id: string;
  text: string;
}

export interface TreatmentPlanAppointment {
  id: string;
  name: string;
  serviceId: string;
  category: string;
  duration: string;
  roomId: string;
  room?: string;
  intervalValue: string;
  intervalUnit: "Days" | "Weeks" | "Months";
  isRecurring: boolean;
  recurrenceFrequency: "Weekly" | "Monthly" | "Yearly";
  recurrenceDurationValue: string;
  recurrenceDurationUnit: "Weeks" | "Months" | "Years";
  sendPreConsultationForm: boolean;
  preConsultationTemplateId: string;
  preConsultationSendValue: string;
  preConsultationSendUnit: "Hours" | "Days";
  tasks: TreatmentPlanTask[];
}

interface CommunityTemplate extends ConsultationTemplate {
  author: {
    id: string;
    name: string;
    avatar?: string;
    verified: boolean;
  };
  downloads: number;
  rating: number;
  reviews: number;
  tags: string[];
  isOwned: false;
}

export interface ConsultationSection {
  id: string;
  title: string;
  questions: ConsultationQuestion[];
}

export interface ConsultationQuestion {
  id: string;
  question: string;
  required: boolean;
  value: string;
}

// Inline builder errors, keyed by position:
// `title-${si}`, `questions-${si}`, `question-${si}-${qi}`.
export type TemplateSectionErrors = Record<string, string>;

// Flags sections with a blank title or no questions, and questions with a blank
// label. Completely empty sections (blank title and no non-blank questions) are
// skipped: stripAiTemplateUserControlIds drops them before saving.
export const validateTemplateSections = (
  sections: ConsultationSection[] = [],
): { errors: TemplateSectionErrors; sectionIdsWithErrors: string[] } => {
  const isBlank = (value?: string | null) => !String(value ?? "").trim();
  const errors: TemplateSectionErrors = {};
  const sectionIdsWithErrors: string[] = [];

  sections.forEach((section, si) => {
    const questions = Array.isArray(section.questions) ? section.questions : [];
    const titleBlank = isBlank(section.title);
    if (titleBlank && questions.every((q) => isBlank(q.question))) return;

    let hasError = false;
    if (titleBlank) {
      errors[`title-${si}`] = "Section title is required.";
      hasError = true;
    }
    if (questions.length === 0) {
      errors[`questions-${si}`] = "Add at least one question to this section.";
      hasError = true;
    }
    questions.forEach((q, qi) => {
      if (isBlank(q.question)) {
        errors[`question-${si}-${qi}`] = "Question cannot be empty.";
        hasError = true;
      }
    });
    if (hasError) sectionIdsWithErrors.push(section.id);
  });

  return { errors, sectionIdsWithErrors };
};

export interface ConsultationAction {
  id: string;
  label: string;
  description?: string;
  enabled: boolean;
}

export interface ReviewData {
  reviewNotes: string;
  timestamp: string;
}

// Reverse maps: API returns integers, selects expect string literals
const intervalUnitIntToStr: Record<number, "Days" | "Weeks" | "Months"> = {
  1: "Days",
  2: "Weeks",
  3: "Months",
};
const recurrenceFreqIntToStr: Record<
  number,
  "Weekly" | "Monthly" | "Yearly"
> = { 1: "Weekly", 2: "Monthly", 3: "Yearly" };
const recurrenceDurUnitIntToStr: Record<
  number,
  "Weeks" | "Months" | "Years"
> = { 1: "Weeks", 2: "Months", 3: "Years" };
const preConsultSendUnitIntToStr: Record<number, "Hours" | "Days"> = {
  1: "Hours",
  2: "Days",
};

export const useConsultationsData = () => {
  const [initialTemplates, setTemplates] = useState<ConsultationTemplate[]>([]);
  const [
    generatedTemplates,
    setGeneratedTemplates,
  ] = useState<ConsultationTemplate | null>(null);
  const {
    appState,
    templateState,
    patientState,
    generatePreConsultAiTemplateUrl,
    companyDocumentState,
  } = useTitanApp();
  const {
    currentAiTemplates,
    isConsultationLoading,
    aiTemplate,
    isDropdownAiTemplatesLoading,
    isAiTemplateLoading,
    isSaveAiTemplateLoading,
  } = templateState || {};
  const {
    patientConsultaionCountData,
    serviceSummary,
    isPatientConsultaionCountLoading,
    allDocuments,
    serviceSummaryLoading,
  } = patientState || {};
  const { darkMode, companyId, currentUser } = appState || {};
  const { allCompanyDocuments } = companyDocumentState || {};
  const [buttonDisabled, setButtonDisabled] = useState(false);

  useEffect(() => {
    if (currentAiTemplates && currentAiTemplates.length > 0) {
      const mappedTemplates: ConsultationTemplate[] = currentAiTemplates.map(
        (t: any) => ({
          id: t._id,
          name: t.templateName || "",
          description: "",
          consultationSummary: t.consultationSummary || "",
          category: "general",
          sections: t.templateSections.map((s: any, sectionIndex: number) => ({
            id: s.groupId ?? `section-${sectionIndex}`,
            title: s.sectionTitle,
            questions: s.userControls.map((q: any, index: number) => ({
              id: `${s.sectionTitle}-${index}`,
              question: q.label,
              required: q.required,
              value: q.value,
            })),
          })),
          isDefault: t.isDefault || false,
          isActive: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          usageCount: patientConsultaionCountData?.[t._id] || 0,
          isOwned: true,
          linkedDocumentId: t.linkedDocumentId || "",
          forceUserSignOff: t.forceUserSignOff || false,
          lastReviewedAt: t.lastReviewedAt,
          reviewNote: t.reviewNote,
          consultationActions: t.consultationActions || [],
          treatmentPlan: t.treatmentPlan
            ? {
                treatmentPlanConfigurationId:
                  t.treatmentPlan.TreatmentPlanConfigurationId ||
                  t.treatmentPlan.treatmentPlanConfigurationId ||
                  "",
                appointments: (
                  t.treatmentPlan.appointments ||
                  t.treatmentPlan.Appointments ||
                  t.treatmentPlan.Appointment ||
                  []
                ).map((a: any) => ({
                  id: a.id || a._id || `appt_${Math.random()}`,
                  name: a.name || a.Name || "",
                  serviceId: a.serviceId || a.ServiceId || a.ServiceID || "",
                  category: a.category || a.Category || "",
                  duration: String(a.duration ?? a.Duration ?? ""),
                  roomId: a.roomId || a.RoomId || "",
                  isRecurring: a.isRecurring ?? a.IsRecurring ?? false,
                  intervalUnit:
                    intervalUnitIntToStr[a.intervalUnit] ||
                    a.intervalUnit ||
                    "Weeks",
                  intervalValue: a.intervalValue || a.IntervalValue || "",
                  recurrenceFrequency:
                    recurrenceFreqIntToStr[a.recurrenceFrequency] ||
                    a.recurrenceFrequency ||
                    "Monthly",
                  recurrenceDurationValue: a.recurrenceDurationValue || "",
                  recurrenceDurationUnit:
                    recurrenceDurUnitIntToStr[a.recurrenceDurationUnit] ||
                    a.recurrenceDurationUnit ||
                    "Months",
                  sendPreConsultationForm:
                    a.sendPreConsultationForm ??
                    a.SendPreConsultationForm ??
                    false,
                  preConsultationTemplateId:
                    a.preConsultationTemplateId ||
                    a.PreConsultationTemplateId ||
                    "",
                  preConsultationSendValue:
                    a.preConsultationSendValue ||
                    a.PreConsultationSendValue ||
                    "",
                  preConsultationSendUnit:
                    preConsultSendUnitIntToStr[a.preConsultationSendUnit] ||
                    a.preConsultationSendUnit ||
                    "Hours",
                  tasks: (a.tasks || a.Tasks || []).map((task: any) => ({
                    id: task.id || task.Id || "",
                    text: task.text || task.Text || "",
                  })),
                })),
              }
            : undefined,
        }),
      );
      setTemplates(mappedTemplates);
    }
  }, [currentAiTemplates, patientConsultaionCountData]);

  useEffect(() => {
    if (aiTemplate && aiTemplate.templateSections) {
      const mappedTemplate: ConsultationTemplate = {
        id: aiTemplate._id || "",
        name: aiTemplate.templateName || "",
        description: "",
        consultationSummary: aiTemplate.consultationSummary || "",
        companyID: aiTemplate.companyID || "",
        category: "general",
        sections: aiTemplate.templateSections.map((section: any) => ({
          id: section.groupId || "",
          title: section.sectionTitle || "",
          questions: section.userControls.map((q: any, index: number) => ({
            id: `${section.sectionTitle}-${index}`,
            question: q.label || "",
            required: !!q.required,
            value: q.value || "",
          })),
        })),
        isDefault: aiTemplate.isDefault || false,
        isActive: true,
        createdAt: aiTemplate.createdOn || new Date().toISOString(),
        updatedAt: aiTemplate.updatedOn || new Date().toISOString(),
        createdBy: aiTemplate.createdBy || "",
        createdByName: aiTemplate.createdByName || "",
        updatedBy: aiTemplate.updatedBy || "",
        updatedByName: aiTemplate.updatedByName || "",
        archiveAt: aiTemplate.archiveOn || "",
        archiveBy: aiTemplate.archiveBy || "",
        archiveByName: aiTemplate.archiveByName || "",
        usageCount: patientConsultaionCountData?.[aiTemplate._id] || 0,
        isOwned: true,
        isArchive: aiTemplate.isArchive || false,
        tempPrompt: "",
        linkedDocumentId: aiTemplate.linkedDocumentId || "",
        forceUserSignOff: aiTemplate.forceUserSignOff || false,
        consultationActions: aiTemplate.consultationActions || [], // YE ADD KARO
        treatmentPlan: aiTemplate.treatmentPlan
          ? {
              treatmentPlanConfigurationId:
                aiTemplate.treatmentPlan.TreatmentPlanConfigurationId ||
                aiTemplate.treatmentPlan.treatmentPlanConfigurationId ||
                "",
              appointments: (
                aiTemplate.treatmentPlan.appointments ||
                aiTemplate.treatmentPlan.Appointments ||
                aiTemplate.treatmentPlan.Appointment ||
                []
              ).map((a: any) => ({
                id: a.id || a._id || `appt_${Math.random()}`,
                name: a.name || a.Name || "",
                serviceId: a.serviceId || a.ServiceId || a.ServiceID || "",
                category: a.category || a.Category || "",
                duration: String(a.duration ?? a.Duration ?? ""),
                roomId: a.roomId || a.RoomId || "",
                isRecurring: a.isRecurring ?? a.IsRecurring ?? false,
                intervalUnit:
                  intervalUnitIntToStr[a.intervalUnit] ||
                  a.intervalUnit ||
                  "Weeks",
                intervalValue: a.intervalValue || a.IntervalValue || "",
                recurrenceFrequency:
                  recurrenceFreqIntToStr[a.recurrenceFrequency] ||
                  a.recurrenceFrequency ||
                  "Monthly",
                recurrenceDurationValue: a.recurrenceDurationValue || "",
                recurrenceDurationUnit:
                  recurrenceDurUnitIntToStr[a.recurrenceDurationUnit] ||
                  a.recurrenceDurationUnit ||
                  "Months",
                sendPreConsultationForm:
                  a.sendPreConsultationForm ??
                  a.SendPreConsultationForm ??
                  false,
                preConsultationTemplateId:
                  a.preConsultationTemplateId ||
                  a.PreConsultationTemplateId ||
                  "",
                preConsultationSendValue:
                  a.preConsultationSendValue ||
                  a.PreConsultationSendValue ||
                  "",
                preConsultationSendUnit:
                  preConsultSendUnitIntToStr[a.preConsultationSendUnit] ||
                  a.preConsultationSendUnit ||
                  "Hours",
                tasks: (a.tasks || a.Tasks || []).map((task: any) => ({
                  id: task.id || task.Id || "",
                  text: task.text || task.Text || "",
                })),
              })),
            }
          : undefined,
      };

      setGeneratedTemplates(mappedTemplate);
    }
  }, [aiTemplate]);

  const generateConsultationTemplate = async (
    prompt: string,
    attachment?: string,
  ) => {
    const params: { userPrompt: string; attachment?: string } = {
      userPrompt: prompt,
    };
    if (attachment) params.attachment = attachment;
    await generateAiTemplate(params);
  };

  const getServiceSummaryStream = async (
    prompt: string,
    setIsStreaming: (val: boolean) => void,
    setSummary: (summary: string) => void,
    attachment?: string,
  ) => {
    try {
      setIsStreaming(true);
      let accumulatedText = "";

      const params: { prompt: string; attachment?: string } = { prompt };
      if (attachment) params.attachment = attachment;

      await AiStreamResponse(
        params,
        (chunk: string) => {
          accumulatedText += chunk;
          setConsultationStreamedSummary(accumulatedText);
          setSummary(accumulatedText);
        },
        () => {
          setIsStreaming(false);
          setConsultationStreamedSummary(accumulatedText);
          setSummary(accumulatedText);
        },
        generateAiServiceTemplateUrl(prompt),
      );
    } catch (error) {
      console.error("Streaming failed", error);
      setIsStreaming(false);
    }
  };

  const intervalUnitMap: Record<string, number> = {
    Days: 1,
    Weeks: 2,
    Months: 3,
  };
  const recurrenceFrequencyMap: Record<string, number> = {
    Weekly: 1,
    Monthly: 2,
    Yearly: 3,
  };
  const preConsultSendUnitMap: Record<string, number> = { Hours: 1, Days: 2 };
  const recurrenceDurationUnitMap: Record<string, number> = {
    Weeks: 1,
    Months: 2,
    Years: 3,
  };

  const handleTemplateSubmit = async (
    formData: ConsultationTemplate,
    onSave: (templateData: Partial<ConsultationTemplate>) => void,
  ) => {
    setButtonDisabled(true);
    if (!formData?.name.trim()) {
      alert("Please enter a template name");
      return;
    }
    if (!formData?.consultationSummary?.trim()) {
      alert("Consultation summary cannot be empty");
      setButtonDisabled(false);
      return;
    }

    try {
      if (formData?.id) {
        // Update existing template
        const payload = {
          _id: formData?.id,
          templateName: formData?.name,
          companyID: companyId,
          consultationSummary: formData?.consultationSummary,
          linkedDocumentId: formData?.linkedDocumentId || "",
          forceUserSignOff: formData?.forceUserSignOff || false,
          lastReviewedAt: formData?.lastReviewedAt,
          reviewNote: formData?.reviewNote,
          templateSections: formData?.sections?.map((section) => ({
            groupId: null,
            sectionTitle: section?.title,
            userControls: section?.questions.map((control) => ({
              label: control?.question,
              required: control?.required ?? true,
              value: control?.value ?? null,
              userControlId: null,
            })),
          })),
          consultationActions: (formData?.consultationActions || []).map(
            (action) => ({
              id: action.id,
              label: action.label,
              enabled: action.enabled,
            }),
          ),
          treatmentPlan:
            (formData?.treatmentPlan?.appointments?.length ?? 0) > 0
              ? {
                  companyId: companyId,
                  createdBy: currentUser?.fullName,
                  createdByName: currentUser?.emailAddress,
                  updatedBy: currentUser?.fullName,
                  updatedByName: currentUser?.fullName,
                  appointments: formData.treatmentPlan!.appointments.map(
                    (a) => ({
                      name: a.name,
                      serviceId: a.serviceId,
                      category: a.category,
                      duration: parseInt(a.duration, 10) || 0,
                      roomId: a.roomId,
                      room: a.room || "",
                      isRecurring: a.isRecurring,
                      intervalUnit: intervalUnitMap[a.intervalUnit] ?? null,
                      intervalValue: a.intervalValue,
                      recurrenceFrequency:
                        recurrenceFrequencyMap[a.recurrenceFrequency] ?? null,
                      recurrenceDurationValue:
                        a.recurrenceDurationValue || null,
                      recurrenceDurationUnit:
                        recurrenceDurationUnitMap[a.recurrenceDurationUnit] ??
                        null,
                      sendPreConsultationForm: a.sendPreConsultationForm,
                      preConsultationTemplateId: a.preConsultationTemplateId,
                      preConsultationSendValue: a.preConsultationSendValue,
                      preConsultationSendUnit:
                        preConsultSendUnitMap[a.preConsultationSendUnit] ??
                        null,
                      tasks: (a.tasks || []).map((t) => ({
                        id: t.id,
                        text: t.text,
                      })),
                    }),
                  ),
                }
              : null,
        };

        await updateAiTemplates(payload);
      } else {
        // Create new template
        const payload = {
          templateName: formData?.name,
          companyID: companyId,
          consultationSummary: formData?.consultationSummary,
          linkedDocumentId: formData?.linkedDocumentId || "",
          forceUserSignOff: formData?.forceUserSignOff || false,
          templateSections: formData?.sections?.map((section) => ({
            groupId: null,
            sectionTitle: section?.title,
            userControls: section?.questions.map((control) => ({
              label: control?.question,
              required: control?.required ?? true,
              value: control?.value ?? null,
              userControlId: null,
            })),
          })),
          consultationActions: (formData?.consultationActions || []).map(
            (action) => ({
              // YE ADD KARO
              id: action.id,
              label: action.label,
              enabled: action.enabled,
            }),
          ),
          treatmentPlan:
            (formData?.treatmentPlan?.appointments?.length ?? 0) > 0
              ? {
                  companyId: companyId,
                  appointments: formData.treatmentPlan!.appointments.map(
                    (a) => ({
                      name: a.name,
                      serviceId: a.serviceId,
                      category: a.category,
                      duration: parseInt(a.duration, 10) || 0,
                      roomId: a.roomId,
                      room: a.room || "",
                      isRecurring: a.isRecurring,
                      intervalUnit: intervalUnitMap[a.intervalUnit] ?? null,
                      intervalValue: a.intervalValue,
                      recurrenceFrequency:
                        recurrenceFrequencyMap[a.recurrenceFrequency] ?? null,
                      recurrenceDurationValue:
                        a.recurrenceDurationValue || null,
                      recurrenceDurationUnit:
                        recurrenceDurationUnitMap[a.recurrenceDurationUnit] ??
                        null,
                      sendPreConsultationForm: a.sendPreConsultationForm,
                      preConsultationTemplateId: a.preConsultationTemplateId,
                      preConsultationSendValue: a.preConsultationSendValue,
                      preConsultationSendUnit:
                        preConsultSendUnitMap[a.preConsultationSendUnit] ??
                        null,
                      tasks: (a.tasks || []).map((t) => ({
                        id: t.id,
                        text: t.text,
                      })),
                    }),
                  ),
                }
              : null,
        };
        await saveAiTemplate(payload);
      }

      // Refresh data after save/update
      await getAiTemplateByCompanyId(companyId);
      // await getPatientConsultationsCount(companyId);
      setButtonDisabled(false);
      onSave(formData);
    } catch (error) {
      console.error("Error saving template:", error);
      alert("Failed to save template. Please try again.");
    }
  };

  const hasRequiredHeading = (text?: string | null): boolean => {
    if (!text) return false;

    const headingRegex = /(^|\n)\s*(?:Presenting Complaint|Medical History|Medication History|Allergies|Family History|Social History|Observations? (?:and|&) Assessments?|Advice (?:and|&) Counsell?ing Provided|Follow-?Up Plan)\s*:*/i;

    return headingRegex.test(text);
  };

  return {
    darkMode,
    initialTemplates,
    generatedTemplates,
    isConsultationLoading,
    generateConsultationTemplate,
    getServiceSummaryStream,
    isDropdownAiTemplatesLoading,
    setGeneratedTemplates,
    setTemplates,
    handleTemplateSubmit,
    isAiTemplateLoading,
    isSaveAiTemplateLoading,
    isPatientConsultaionCountLoading,
    buttonDisabled,
    setIsConsultationSummaryLoading,
    allDocuments,
    allCompanyDocuments,
    serviceSummaryLoading,
    hasRequiredHeading,
    currentUser,
  };
};
